import { get, post, put, del } from './client'
import type { ApiKey } from '@/types'

export interface ApiKeyListParams {
  page?: number
  page_size?: number
}

export interface ApiKeyListResponse {
  items: ApiKey[]
  total: number
  page: number
  page_size: number
}

export interface CreateApiKeyParams {
  name: string
  remain_quota?: number
  unlimited_quota?: boolean
  expired_time?: number
  model_limits_enabled?: boolean
  /** 后端为逗号分隔的单个 string */
  model_limits?: string
  group?: string
  allow_ips?: string
  cross_group_retry?: boolean
}

export interface UpdateApiKeyParams {
  id: number
  name?: string
  remain_quota?: number
  unlimited_quota?: boolean
  expired_time?: number
  model_limits_enabled?: boolean
  model_limits?: string
  group?: string
  allow_ips?: string
  cross_group_retry?: boolean
  status?: number
}

// ==================== API Key 管理 ====================

/**
 * 获取 API Key 列表
 * GET /api/token
 */
export async function getApiKeysApi(params?: ApiKeyListParams): Promise<ApiKeyListResponse> {
  return get<ApiKeyListResponse>('/api/token', params)
}

/**
 * 搜索 API Key
 * GET /api/token/search
 */
export async function searchApiKeysApi(keyword: string): Promise<ApiKey[]> {
  return get<ApiKey[]>('/api/token/search', { keyword })
}

/**
 * 获取单个 API Key 详情
 * GET /api/token/:id
 */
export async function getApiKeyDetailApi(id: number): Promise<ApiKey> {
  return get<ApiKey>(`/api/token/${id}`)
}

/**
 * 创建 API Key
 * POST /api/token
 * 注意：创建成功后会返回完整的 key，这是唯一一次能看到完整 key 的机会
 */
export async function createApiKeyApi(data: CreateApiKeyParams): Promise<ApiKey> {
  return post<ApiKey>('/api/token', data)
}

/**
 * 更新 API Key
 * PUT /api/token
 */
export async function updateApiKeyApi(data: UpdateApiKeyParams): Promise<void> {
  return put('/api/token', data)
}

/**
 * 仅更新 API Key 状态（启用/禁用）
 * PUT /api/token/?status_only=true
 */
export async function updateApiKeyStatusApi(id: number, status: number): Promise<void> {
  return put('/api/token/?status_only=true', { id, status })
}

/**
 * 删除 API Key
 * DELETE /api/token/:id
 */
export async function deleteApiKeyApi(id: number): Promise<void> {
  return del(`/api/token/${id}`)
}

/**
 * 批量删除 API Key
 * POST /api/token/batch
 */
export async function batchDeleteApiKeysApi(ids: number[]): Promise<{ count: number }> {
  return post('/api/token/batch', { ids })
}

/**
 * 获取 API Key 的完整密钥
 * POST /api/token/:id/key
 * 注意：这是一个敏感操作，可能需要额外验证
 */
export async function getApiKeySecretApi(id: number): Promise<{ key: string }> {
  return post(`/api/token/${id}/key`)
}

/**
 * 批量获取 API Key 密钥
 * POST /api/token/batch/keys
 */
export async function batchGetApiKeySecretsApi(ids: number[]): Promise<Record<number, string>> {
  return post('/api/token/batch/keys', { ids })
}
